// #3185 APPROVE.2 PR 2c (recon §6) — "Client approvals for this project":
// the per-project override that replaces ApprovalConfigPage's old "Client
// Approval Requirements" toggles. GET/PUT /projects/:id/client-approval-overrides
// (PR 2b). Shown to admin rank (TENANT_ADMIN or legacy ADMIN) and, in an
// ACADEMIC workspace only, FACULTY_SUPERVISOR (ruling D10); the route refuses
// everyone else, so nobody else is shown a control that can only fail.
// "Use workspace default" is stored as no override row (PUT null). Saved
// separately from the page's approval mode and windows (a different endpoint).
// Codex R2 finding 2 (ruling 6537648337 item 2): the role above is the selected
// workspace's, so the panel acts only on that workspace's projects. For a
// project in another workspace it shows a switch prompt instead (fail closed);
// the project's workspace comes from GET /projects/:id, an existing read.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { useMyTenant, isAdminRole } from '../../hooks/useMyTenant';
import ClientApprovalMatrixTable from './ClientApprovalMatrixTable';
import PreEnforcementNotice from './PreEnforcementNotice';
import OtherWorkspacePrompt, { projectWorkspace } from './OtherWorkspacePrompt';
import { clientApprovalTypeLabel, platformDefaultRequired, requiredLabel } from '../../lib/clientApprovals';

type Matrix = Record<string, boolean>;
type Choice = 'default' | 'required' | 'not_required';

const toChoice = (v: boolean | undefined): Choice => (v === undefined ? 'default' : v ? 'required' : 'not_required');
const fromChoice = (c: Choice): boolean | null => (c === 'default' ? null : c === 'required');

/** Ruling D10: who may change a project's client approval override. */
export function canEditClientApprovalOverride(role: string | undefined, subscriptionTier: string | undefined): boolean {
  return isAdminRole(role) || (subscriptionTier === 'ACADEMIC' && role === 'FACULTY_SUPERVISOR');
}

const selectStyle: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
  background: 'var(--surface-alt)', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 13,
};

interface Props {
  projectId: string;
  cardStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  descStyle: React.CSSProperties;
}

export default function ProjectClientApprovalOverridePanel({ projectId, cardStyle, labelStyle, descStyle }: Props) {
  const { myTenant } = useMyTenant();
  const [projectTenantId, setProjectTenantId] = useState<string | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);
  const workspace = projectWorkspace(projectTenantId, myTenant?.tenantId);
  const allowed = workspace === 'selected' && canEditClientApprovalOverride(myTenant?.role, myTenant?.subscriptionTier);

  const [types, setTypes] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<Matrix>({});
  const [defaults, setDefaults] = useState<Matrix>({});
  const [draft, setDraft] = useState<Record<string, Choice>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiFetch<{ overrides: Matrix; defaults: Matrix; types: string[] }>(
        `/projects/${projectId}/client-approval-overrides`,
      );
      setTypes(data.types);
      setOverrides(data.overrides);
      setDefaults(data.defaults);
      setDraft(Object.fromEntries(data.types.map((t) => [t, toChoice(data.overrides[t])])));
    } catch (err) {
      setLoadError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    setProjectTenantId(null);
    setProjectError(null);
    apiFetch<{ tenantId?: string }>(`/projects/${projectId}`)
      .then((p) => { if (!cancelled) setProjectTenantId(p.tenantId ?? null); })
      .catch((err) => { if (!cancelled) setProjectError((err as Error).message); });
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  const changes = useMemo(() => {
    const out: Record<string, boolean | null> = {};
    for (const t of types) {
      if (draft[t] !== undefined && draft[t] !== toChoice(overrides[t])) out[t] = fromChoice(draft[t]);
    }
    return out;
  }, [types, draft, overrides]);
  const dirty = Object.keys(changes).length > 0;

  if (workspace === 'other' && projectTenantId) {
    return (
      <div style={cardStyle} data-testid="client-approval-override-panel">
        <h2 style={labelStyle}>Client approvals for this project</h2>
        <OtherWorkspacePrompt projectTenantId={projectTenantId} />
      </div>
    );
  }
  if (projectError && canEditClientApprovalOverride(myTenant?.role, myTenant?.subscriptionTier)) {
    return (
      <div style={cardStyle} data-testid="client-approval-override-panel">
        <h2 style={labelStyle}>Client approvals for this project</h2>
        <div role="alert" style={{ fontSize: 13, color: '#dc2626' }}>
          Couldn&rsquo;t load this project: {projectError}
        </div>
      </div>
    );
  }
  if (!allowed) return null;

  const workspaceDefault = (t: string) => defaults[t] ?? platformDefaultRequired(t);

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    setSaveError(null);
    setSavedFlash(false);
    try {
      const data = await apiFetch<{ overrides: Matrix }>(`/projects/${projectId}/client-approval-overrides`, {
        method: 'PUT',
        body: JSON.stringify({ overrides: changes }),
      });
      setOverrides(data.overrides);
      setDraft(Object.fromEntries(types.map((t) => [t, toChoice(data.overrides[t])])));
      setSavedFlash(true);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle} data-testid="client-approval-override-panel">
      <h2 style={labelStyle}>Client approvals for this project</h2>
      <p style={descStyle}>
        Override the workspace default for this project. Without an override, client approval applies only when the
        project has a client stakeholder. Changes apply to versions submitted after you save.
      </p>
      <PreEnforcementNotice />

      {loading ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
      ) : loadError ? (
        <div role="alert" style={{ fontSize: 13, color: '#dc2626' }}>
          Couldn&rsquo;t load client approval overrides: {loadError}
        </div>
      ) : (
        <>
          <ClientApprovalMatrixTable
            testId="client-approval-override-matrix"
            types={types}
            hint={(t) => (overrides[t] !== undefined
              ? 'Overridden for this project'
              : `${defaults[t] !== undefined ? 'Workspace' : 'Platform'} default: ${requiredLabel(workspaceDefault(t)).toLowerCase()}`)}
            renderControl={(t) => (
              <select
                aria-label={`Client approval for ${clientApprovalTypeLabel(t)}`}
                data-testid={`override-select-${t}`}
                value={draft[t] ?? 'default'}
                onChange={(e) => { setDraft((d) => ({ ...d, [t]: e.target.value as Choice })); setSavedFlash(false); }}
                // Codex R2 finding 4: locked while saving, so the response can't overwrite a later edit.
                disabled={saving}
                style={selectStyle}
              >
                <option value="default">Use workspace default ({requiredLabel(workspaceDefault(t))})</option>
                <option value="required">Required</option>
                <option value="not_required">Not required</option>
              </select>
            )}
          />
          {saveError && (
            <div role="alert" style={{ marginTop: 12, fontSize: 13, color: '#dc2626' }}>{saveError}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving}
              style={{
                padding: '8px 20px', borderRadius: 'var(--radius-md)', background: 'var(--accent)', color: '#fff',
                border: 'none', fontWeight: 600, fontSize: 14,
                cursor: !dirty || saving ? 'not-allowed' : 'pointer', opacity: !dirty || saving ? 0.5 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save client approvals'}
            </button>
            {savedFlash && <span role="status" style={{ fontSize: 13, color: '#16a34a' }}>Saved.</span>}
            {/* R1 P3-6: the page's Save Configuration doesn't save this panel. */}
            {dirty && !saving && (
              <span data-testid="override-unsaved" style={{ fontSize: 13, color: '#d97706' }}>
                Unsaved. Save client approvals here; Save Configuration doesn&rsquo;t include them.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
